import { DownloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Descriptions, Row, Skeleton, Space, Statistic, Table, Tag, message } from 'antd';
import { downloadFile, errorMessage } from '../../api/client';
import { useFixedAccount } from '../../api/hooks';
import type { FixedLedgerEntry, PartyType } from '../../types';
import { date, money } from '../../utils/format';

const TYPE_COLOR: Record<string, string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
};

interface Props {
  partyType: PartyType;
  partyId: number;
}

/**
 * The fixed-return ledger — every investment and withdrawal in date order with a running balance.
 * Laid out like the percentage Ledger tab so both read the same way, but the columns describe an
 * agreed total being drawn down rather than capital plus accruing profit.
 */
export default function FixedLedgerTab({ partyType, partyId }: Props) {
  const { data: account, isLoading, error } = useFixedAccount(partyType, partyId);

  if (error) return <Alert type="error" showIcon message={errorMessage(error)} />;
  if (isLoading || !account) return <Skeleton active paragraph={{ rows: 6 }} />;

  const statementPath =
    partyType === 'Investor'
      ? `/reports/fixed-statement/investor/${partyId}`
      : `/reports/fixed-statement/dealer/${partyId}`;

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() =>
            downloadFile(statementPath, `fixed_statement_${partyId}.pdf`).catch((err) =>
              message.error(errorMessage(err)),
            )
          }
        >
          Download statement (PDF)
        </Button>
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Total invested" value={money(account.totalInvested)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Total return agreed" value={money(account.totalReturnAgreed)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Remaining to return"
              value={money(account.totalRemaining)}
              prefix="PKR"
              valueStyle={{ color: account.totalRemaining > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      <Descriptions bordered size="small" column={{ xs: 1, md: 2 }} style={{ marginTop: 16 }}>
        <Descriptions.Item label="Total profit agreed">{money(account.totalProfit)}</Descriptions.Item>
        <Descriptions.Item label="Total withdrawn">{money(account.totalWithdrawn)}</Descriptions.Item>
        <Descriptions.Item label="Investments on record">{account.investments.length}</Descriptions.Item>
        <Descriptions.Item label="Next due">
          {account.nextDueDate ? date(account.nextDueDate) : '—'}
        </Descriptions.Item>
      </Descriptions>

      <Card title="Transactions" size="small" style={{ marginTop: 16 }}>
        <Table<FixedLedgerEntry>
          rowKey={(row) => `${row.type}-${row.refId}`}
          size="small"
          dataSource={account.ledger}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          columns={[
            { title: 'Date', dataIndex: 'date', render: date },
            {
              title: 'Type',
              dataIndex: 'type',
              render: (v: string) => <Tag color={TYPE_COLOR[v] ?? 'default'}>{v}</Tag>,
            },
            { title: 'Description', dataIndex: 'description' },
            {
              title: 'Invested',
              dataIndex: 'invested',
              align: 'right',
              render: (v: number) => (v ? money(v) : '—'),
            },
            {
              title: 'Return agreed',
              dataIndex: 'returnAgreed',
              align: 'right',
              render: (v: number) => (v ? money(v) : '—'),
            },
            {
              title: 'Withdrawn',
              dataIndex: 'withdrawn',
              align: 'right',
              render: (v: number) => (v ? money(v) : '—'),
            },
            {
              title: 'Remaining',
              dataIndex: 'remainingAfter',
              align: 'right',
              render: (v: number) => <strong>{money(v)}</strong>,
            },
          ]}
        />
      </Card>
    </>
  );
}
